import * as React from "react";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import { DataGridPremium, GridColDef } from "@mui/x-data-grid-premium";
import ReplayIcon from "@mui/icons-material/Replay";
import FlexGrid from "../../../layouts/flexGrid";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclDataGridToolbar from "../../../components/common/cclDataGridToolbar/cclDataGridToolbar";
import CclSearchDefault from "../../../components/common/cclSearchDefault";
import ComponentLoader from "../../../components/common/componentLoader";
import { useLazyGetAssessmentQualificationsByEmailQuery } from "../../../services/cclTokenedAssessmentApi";
import { AssessmentQualification } from "../../../services/types/assessmentAPITypes";
import { validateEmail } from "../../../utilities/generalUtilities";

const getColumns = (email: string): GridColDef[] => [
    {
        field: "qualification",
        headerName: "Assessment",
        headerAlign: "left",
        align: "left",
        flex: 2,
    },
    {
        field: "email",
        headerName: "Email",
        headerAlign: "left",
        align: "left",
        flex: 2,
        valueGetter: () => email,
    },
    {
        field: "createdDateTime",
        headerName: "Certified",
        headerAlign: "left",
        align: "left",
        flex: 1,
        valueFormatter: (params: { value: string }) =>
            params.value ? new Date(params.value).toLocaleDateString() : "",
    },
];

const FacilitatorsCard: React.FC = () => {
    const [email, setEmail] = React.useState<string>("");
    const [emailError, setEmailError] = React.useState<string>("");
    const [searchedEmail, setSearchedEmail] = React.useState<string>("");
    const [hasSearched, setHasSearched] = React.useState<boolean>(false);
    const [isSearching, setIsSearching] = React.useState<boolean>(false);
    const [isRealError, setIsRealError] = React.useState<boolean>(false);
    const [qualifications, setQualifications] = React.useState<AssessmentQualification[]>([]);

    const [getQualifications] = useLazyGetAssessmentQualificationsByEmailQuery();

    const executeSearch = () => {
        if (isSearching) return;

        const trimmed = email.trim();
        if (!validateEmail(trimmed)) {
            setEmailError("Not a valid email address");
            return;
        }
        setEmailError("");
        setSearchedEmail(trimmed);
        setHasSearched(true);
        // clear any previously displayed results before the new request goes out
        setQualifications([]);
        setIsRealError(false);
        setIsSearching(true);

        getQualifications(trimmed)
            .unwrap()
            .then((results) => {
                setQualifications(results);
                setIsSearching(false);
            })
            .catch((err) => {
                const isNotFound = !!err && "status" in err && err.status === 404;
                setIsRealError(!isNotFound);
                setIsSearching(false);
            });
    };

    const resetSearch = () => {
        if (isSearching) return;

        setEmail("");
        setEmailError("");
        setSearchedEmail("");
        setHasSearched(false);
        setQualifications([]);
        setIsRealError(false);
    };

    const handleKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === "Enter") {
            evt.preventDefault();
            executeSearch();
        }
    };

    const columns = getColumns(searchedEmail);

    return (
        <Stack width={1} height={1} spacing={2}>
            <Box
                gap={2}
                sx={{
                    p: 2,
                    mb: 5,
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                }}
            >
                <TextField
                    value={email}
                    label="Enter Facilitator Email"
                    variant="outlined"
                    fullWidth
                    error={!!emailError}
                    helperText={emailError}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError("");
                    }}
                    onKeyDown={handleKeyDown}
                    sx={{
                        flexGrow: 1,
                        "& .MuiOutlinedInput-input": {
                            paddingTop: "8px!important",
                            paddingBottom: "8px!important",
                        },
                        "& .MuiInputLabel-root": {
                            marginTop: "-8px!important",
                        },
                        "& .MuiInputLabel-shrink": {
                            marginTop: "auto!important",
                        },
                    }}
                />
                <CclButton
                    mode="secondary"
                    aria-label="Reset search"
                    onClick={resetSearch}
                    disabled={isSearching}
                    sx={{
                        borderRadius: "100%",
                        maxWidth: "15px",
                        minWidth: "10px",
                        pl: "20px",
                        pr: "20px",
                        lineHeight: "20px",
                        letterSpacing: "0.1px",
                        gap: "6px",
                        padding: "8px 4px",
                    }}
                >
                    <ReplayIcon fontSize="small" />
                </CclButton>
                <CclButton
                    onClick={executeSearch}
                    aria-label="Search"
                    size="small"
                    mode="primary"
                    disabled={email.trim() === "" || isSearching}
                    sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                    Search
                </CclButton>
            </Box>
            {isSearching ? (
                <ComponentLoader msg="Loading" />
            ) : !hasSearched ? (
                <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                    <CclSearchDefault
                        line1="Start searching for Facilitators"
                        line2="Enter a Facilitator's Email and click Search"
                        backgroundImageUrl="https://coreprodeuscclstg.blob.core.windows.net/public-web-assets/access-assets/page-description-images/Facilitators.jpg"
                        hasButtons={false}
                    />
                </Box>
            ) : isRealError ? (
                <Alert severity="error" sx={{ width: "100%" }}>
                    There was an issue retrieving qualifications. Please try again.
                </Alert>
            ) : (
                <Stack spacing={2} sx={{ height: "100%", width: "100%" }}>
                    <Typography variant="subtitle1" sx={{ pl: 1, fontWeight: "bold" }}>
                        Qualifications
                    </Typography>
                    <FlexGrid>
                        <DataGridPremium
                            getRowId={(row) => row.aqKey}
                            rows={qualifications}
                            columns={columns}
                            autoHeight
                            disableSelectionOnClick
                            components={{ Toolbar: CclDataGridToolbar }}
                            componentsProps={{
                                toolbar: {
                                    showSaveState: false,
                                    hideSettingsButtons: true,
                                },
                            }}
                        />
                    </FlexGrid>
                </Stack>
            )}
        </Stack>
    );
};

export default FacilitatorsCard;
